import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { FaUserCircle } from "react-icons/fa";
import { FiHome, FiCalendar, FiFileText, FiBriefcase, FiUser, FiMenu, FiChevronDown, FiTruck } from "react-icons/fi";
import Logo from "../../image/pms-logo-wordmark-dark.svg";
import APIClient from "../../api";
import {
  fetchNotificationBadges,
  markNotificationScopeRead,
} from "../../utils/notificationsApi";
import "./header.css";

function NavBadge({ count }) {
  if (!count || count < 1) return null;
  const n = count > 99 ? "99+" : String(count);
  return (
    <span className="pms-nav-badge" aria-label={`${n} notification(s)`}>
      {n}
    </span>
  );
}

function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  // -------- Plateforme --------
  const isNativeApp = Capacitor.isNativePlatform();

  // -------- Header fixe (app) --------
  const headerRef = useRef(null);
  const [, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (!isNativeApp) return;

    document.body.classList.add("app");

    const prevBottom = document.body.style.paddingBottom;
    const prevTop = document.body.style.paddingTop;

    const updateTopPadding = () => {
      const h = headerRef.current?.offsetHeight || 0;
      setHeaderHeight(h);
      document.body.style.paddingTop = `${h}px`;
    };

    updateTopPadding();

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(updateTopPadding);
      if (headerRef.current) ro.observe(headerRef.current);
    } else {
      window.addEventListener("resize", updateTopPadding);
    }

    document.body.style.paddingBottom = "0px";

    return () => {
      document.body.classList.remove("app");
      document.body.style.paddingBottom = prevBottom;
      document.body.style.paddingTop = prevTop;
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", updateTopPadding);
    };
  }, [isNativeApp]);

  // -------- Menu connecté --------
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // -------- Menu inscription non connecté --------
  const [signupOpen, setSignupOpen] = useState(false);
  const signupRef = useRef(null);

  // -------- Helpers storage --------
  const clean = (v) => {
    if (v === undefined || v === null) return "";
    const s = String(v).trim();
    return s === "null" || s === "undefined" ? "" : s;
  };

  // -------- Auth réactive --------
  const [auth, setAuth] = useState(() => ({
    token: clean(localStorage.getItem("token")),
    role: clean(localStorage.getItem("role")).toLowerCase(),
    userId: clean(localStorage.getItem("userId")),
    abonnement: clean(localStorage.getItem("abonnement")).toLowerCase(),
    proCanPostMissions: clean(localStorage.getItem("proCanPostMissions")) === "true",
    prenom: clean(localStorage.getItem("prenom")),
  }));

  const { token, role, userId, abonnement, proCanPostMissions, prenom } = auth;
  const isAuthenticated = !!token;
  const [proMissionsChecked, setProMissionsChecked] = useState(role !== "pro");

  const [badgeDocs, setBadgeDocs] = useState(0);
  const [badgeRdv, setBadgeRdv] = useState(0);

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const refreshAuthFromStorage = () => {
      setAuth({
        token: clean(localStorage.getItem("token")),
        role: clean(localStorage.getItem("role")).toLowerCase(),
        userId: clean(localStorage.getItem("userId")),
        abonnement: clean(localStorage.getItem("abonnement")).toLowerCase(),
        proCanPostMissions: clean(localStorage.getItem("proCanPostMissions")) === "true",
        prenom: clean(localStorage.getItem("prenom")),
      });
    };

    const onAuthChanged = () => refreshAuthFromStorage();
    const onStorage = (e) => {
      if (!e || ["token", "role", "userId", "abonnement", "proCanPostMissions", "prenom"].includes(e.key)) {
        refreshAuthFromStorage();
      }
    };

    window.addEventListener("auth:changed", onAuthChanged);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || role !== "pro") {
      setProMissionsChecked(true);
      return undefined;
    }
    setProMissionsChecked(false);
    let cancelled = false;
    APIClient.get("/api/users/me")
      .then(({ data }) => {
        if (cancelled) return;
        const canPost =
          data?.proReview?.status === "approved" &&
          data?.proReview?.missionsAccess === true;
        localStorage.setItem("proCanPostMissions", canPost ? "true" : "false");
        setAuth((prev) => ({ ...prev, proCanPostMissions: canPost }));
      })
      .catch(() => {
        localStorage.setItem("proCanPostMissions", "false");
        setAuth((prev) => ({ ...prev, proCanPostMissions: false }));
      })
      .finally(() => {
        if (!cancelled) setProMissionsChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, role]);

  const refreshBadges = useCallback(async () => {
    if (!isAuthenticated || !token || role === "admin") {
      setBadgeDocs(0);
      setBadgeRdv(0);
      return;
    }
    const d = await fetchNotificationBadges();
    setBadgeDocs(d.documents);
    setBadgeRdv(d.rdv);
  }, [isAuthenticated, token, role]);

  useEffect(() => {
    if (!isAuthenticated || role === "admin") return undefined;
    refreshBadges();
    const t = window.setInterval(refreshBadges, 60_000);
    const onRef = () => {
      refreshBadges();
    };
    window.addEventListener("pms:badges:refresh", onRef);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("pms:badges:refresh", onRef);
    };
  }, [isAuthenticated, role, refreshBadges]);

  // -------- Profils pro + annonce --------
  const [, setProfils] = useState([]);
  const [proAnnonce, setProAnnonce] = useState(null);
  const [selectedProfil, setSelectedProfil] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("selectedProfil")) || null;
    } catch {
      return null;
    }
  });

  const fetchProfils = useCallback(async () => {
    if (role !== "pro" || !userId) {
      setProfils([]);
      setProAnnonce(null);
      return;
    }
    try {
      const res = await fetch(`${API}/api/annonces/by-user/${userId}`);
      if (!res.ok) {
        setProfils([]);
        setProAnnonce(null);
        return;
      }
      const raw = await res.json();
      const annonce = Array.isArray(raw) ? raw[0] : raw;
      if (annonce?._id) {
        setProAnnonce({ _id: annonce._id, statut: annonce.statut || "" });
        setProfils(Array.isArray(annonce.profils) ? annonce.profils : []);
      } else {
        setProAnnonce(null);
        setProfils([]);
      }
    } catch {
      setProfils([]);
      setProAnnonce(null);
    }
  }, [API, role, userId]);

  useEffect(() => {
    fetchProfils();

    const onAnnonceUpdated = () => fetchProfils();

    const onAuthChanged = () => {
      fetchProfils();
      try {
        setSelectedProfil(JSON.parse(localStorage.getItem("selectedProfil")) || null);
      } catch {
        setSelectedProfil(null);
      }
    };

    const onProfilSelected = () => {
      try {
        const sel = JSON.parse(localStorage.getItem("selectedProfil")) || null;
        setSelectedProfil(sel);
      } catch {
        setSelectedProfil(null);
      }
    };

    const onStorage = (e) => {
      if (["userId", "token", "selectedProfil"].includes(e.key)) {
        onProfilSelected();
        fetchProfils();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        onProfilSelected();
        fetchProfils();
      }
    };

    window.addEventListener("annonce:updated", onAnnonceUpdated);
    window.addEventListener("auth:changed", onAuthChanged);
    window.addEventListener("profil:selected", onProfilSelected);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("annonce:updated", onAnnonceUpdated);
      window.removeEventListener("auth:changed", onAuthChanged);
      window.removeEventListener("profil:selected", onProfilSelected);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchProfils]);

  // -------- Fermer menus si clic dehors --------
  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }

      if (signupOpen && signupRef.current && !signupRef.current.contains(e.target)) {
        setSignupOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen, signupOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setSignupOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setSignupOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // -------- Helpers --------
  const logout = () => {
    localStorage.clear();
    setAuth({ token: "", role: "", userId: "", abonnement: "", proCanPostMissions: false, prenom: "" });
    window.dispatchEvent(new CustomEvent("auth:changed", { detail: { action: "logout" } }));
    navigate("/");
  };

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

  const hasProAnnonce = Boolean(proAnnonce?._id);
  const annonceReady =
    hasProAnnonce && ["valide", "publiee"].includes(proAnnonce.statut);

  const requireProfil = (action) => {
    if (!annonceReady) {
      alert(
        hasProAnnonce
          ? "⚠️ Votre annonce doit être validée par l'admin avant d'utiliser cette fonctionnalité."
          : "⚠️ Créez et faites valider votre annonce professionnelle d'abord."
      );
      if (hasProAnnonce) navigate(`/pro/listing/edit/${proAnnonce._id}`);
      else navigate("/pro/listing");
      return;
    }
    if (!selectedProfil) {
      alert("⚠️ Veuillez choisir un profil avant d'accéder à cette page.");
      navigate("/pro/profiles/select");
      return;
    }
    action();
  };

  const getProfilMenuLabel = () => {
    if (!hasProAnnonce) return "Poster mon profil pro";
    if (!annonceReady) {
      if (proAnnonce.statut === "en_attente") return "Mon annonce (en validation)";
      if (proAnnonce.statut === "refuse") return "Corriger mon annonce";
      return "Compléter mon annonce";
    }
    if (selectedProfil) {
      return `${selectedProfil.name || "Profil"} — ${selectedProfil.profession || "—"}`;
    }
    return "Choisir un profil";
  };

  const handleProfilMenuClick = () => {
    if (!hasProAnnonce) {
      navigate("/pro/listing");
      return;
    }
    if (!annonceReady) {
      navigate(`/pro/listing/edit/${proAnnonce._id}`);
      return;
    }
    goChoisirProfil();
  };

  const goAnnonces = async () => {
    if (!abonnement) {
      alert("⛔ Vous devez avoir un abonnement actif pour accéder à vos annonces.");
      return navigate("/billing/subscription");
    }
    try {
      const res = await fetch(`${API}/api/annonces/by-user/${userId}`);
      if (!res.ok) throw new Error("Erreur API");
      const data = await res.json();

      if (Array.isArray(data)) {
        if (data.length > 0) navigate(`/pro/listing/edit/${data[0]._id}`);
        else navigate("/pro/listing");
      } else if (data && data._id) {
        navigate(`/pro/listing/edit/${data._id}`);
      } else {
        navigate("/pro/listing");
      }
    } catch {
      navigate("/pro/listing");
    }
  };

  const goDocs = () => {
    markNotificationScopeRead("documents");
    if (role === "pro" && (!abonnement || abonnement === "basic")) {
      alert("⛔ Votre formule ne permet pas l'accès aux documents. Veuillez changer d'abonnement.");
      return navigate("/billing/subscription");
    }
    navigate(`/pro/documents/${userId}`);
  };

  const goRDV = () => {
    markNotificationScopeRead("rdv");
    if (role === "pro") {
      if (!abonnement) {
        alert("⛔ Vous devez avoir un abonnement actif pour accéder aux rendez-vous.");
        return navigate("/billing/subscription");
      }
      return navigate("/pro/calendar");
    }
    return navigate("/client/appointments");
  };

  const goChoisirProfil = () => {
    if (role === "pro") navigate("/pro/profiles/select");
  };

  const onKeyActivate = (e, cb) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      cb();
    }
  };

  return (
    <>
      <div
        ref={headerRef}
        className={`header ${isNativeApp ? "header--app" : "header--web"}`}
        style={isNativeApp ? { position: "fixed", top: 0, left: 0, right: 0, zIndex: 3000 } : undefined}
      >
        <div className="header_container">
          {/* Logo */}
          <div
            className="header_container_button-logo"
            role="button"
            tabIndex={0}
            onClick={() => navigate("/")}
            onKeyDown={(e) => onKeyActivate(e, () => navigate("/"))}
          >
            <img src={Logo} alt="ProMeetService" className="logo_header_" />
          </div>

          {/* Espace milieu */}
          <div />

          {/* DROITE */}
          {!isAuthenticated ? (
            <div className="header_auth-actions">
              <button
                className="header_btn header_btn--login"
                onClick={() => navigate("/auth/login")}
              >
                Connexion
              </button>

              <div
                className="header_auth-group"
                ref={signupRef}
                onMouseEnter={() => setSignupOpen(true)}
                onMouseLeave={() => setSignupOpen(false)}
              >
                <button
                  className="header_btn header_btn--signup"
                  onClick={() => setSignupOpen((v) => !v)}
                  aria-expanded={signupOpen}
                  aria-haspopup="menu"
                >
                  Inscription <FiChevronDown size={16} />
                </button>

                {signupOpen && (
                  <div className="signup_dropdown" role="menu">
                    <div className="signup_dropdown-title">Choisir un type de compte</div>

                    <button
                      className="signup_option signup_option--client"
                      onClick={() => {
                        navigate("/auth/signup/client");
                        setSignupOpen(false);
                      }}
                    >
                      Inscription client
                    </button>

                    <button
                      className="signup_option signup_option--pro"
                      onClick={() => {
                        navigate("/auth/signup/pro");
                        setSignupOpen(false);
                      }}
                    >
                      Inscription pro
                    </button>

                    <button
                      className="signup_option signup_option--partner"
                      onClick={() => {
                        navigate("/auth/signup/partner");
                        setSignupOpen(false);
                      }}
                    >
                      Inscription partner
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="header_user-controls" ref={menuRef}>
              <button
                className="header_hamburger"
                aria-label="Ouvrir le menu"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={() => setMenuOpen((v) => !v)}
                onKeyDown={(e) => onKeyActivate(e, () => setMenuOpen((v) => !v))}
                title="Menu"
              >
                <FiMenu size={22} />
              </button>

              {menuOpen && (
                <div className="menu header_menu-app" role="menu">
                  <div className="menu_header">
                    {role === "admin" ? (
                      <span>Bonjour, Admin</span>
                    ) : (
                      <span>Bonjour, {prenom ? cap(prenom) : "Utilisateur"}</span>
                    )}
                  </div>

                  <button className="menu_item" onClick={() => { navigate("/"); setMenuOpen(false); }}>
                    <FiHome style={{ marginRight: 8 }} /> Accueil
                  </button>

                  {role === "admin" && (
                    <>
                      <button className="menu_item" onClick={() => { navigate("/admin/listings"); setMenuOpen(false); }}>
                        Vérification des annonces
                      </button>
                      <button className="menu_item" onClick={() => { navigate("/admin/pros"); setMenuOpen(false); }}>
                        Vérification des pros
                      </button>
                      <button className="menu_item" onClick={() => { navigate("/admin/partners"); setMenuOpen(false); }}>
                        ProMeet Partners
                      </button>
                      <div className="menu_divider" />
                      <button className="menu_item" onClick={() => { logout(); setMenuOpen(false); }}>
                        Déconnexion
                      </button>
                    </>
                  )}

                  {role === "pro" && (
                    <>
                      <button
                        type="button"
                        className="menu_item"
                        onClick={() => {
                          handleProfilMenuClick();
                          setMenuOpen(false);
                        }}
                      >
                        <FaUserCircle style={{ marginRight: 8 }} />
                        {getProfilMenuLabel()}
                      </button>

                      <button
                        type="button"
                        className="menu_item"
                        onClick={() => {
                          goAnnonces();
                          setMenuOpen(false);
                        }}
                      >
                        <FiBriefcase style={{ marginRight: 8 }} /> Mes Annonces
                      </button>

                      <button
                        type="button"
                        className={`menu_item menu_item--with-badge${
                          annonceReady && selectedProfil ? "" : " menu_item--locked"
                        }`}
                        disabled={!annonceReady || !selectedProfil}
                        onClick={() => {
                          if (!annonceReady || !selectedProfil) return;
                          requireProfil(goRDV);
                          setMenuOpen(false);
                        }}
                      >
                        <span className="menu_item__row">
                          <FiCalendar style={{ marginRight: 8 }} />
                          <span className="menu_item__label">Mes rendez-vous</span>
                          {!isNativeApp && <NavBadge count={badgeRdv} />}
                        </span>
                      </button>

                      <button
                        type="button"
                        className={`menu_item menu_item--with-badge${
                          annonceReady && selectedProfil ? "" : " menu_item--locked"
                        }`}
                        disabled={!annonceReady || !selectedProfil}
                        onClick={() => {
                          if (!annonceReady || !selectedProfil) return;
                          requireProfil(goDocs);
                          setMenuOpen(false);
                        }}
                      >
                        <span className="menu_item__row">
                          <FiFileText style={{ marginRight: 8 }} />
                          <span className="menu_item__label">Espace Documents</span>
                          {!isNativeApp && <NavBadge count={badgeDocs} />}
                        </span>
                      </button>

                      <div className="menu_divider" />

                      {proMissionsChecked && proCanPostMissions && (
                        <button
                          className="menu_item"
                          onClick={() => { navigate("/missions/dashboard"); setMenuOpen(false); }}
                        >
                          <FiTruck style={{ marginRight: 8 }} /> ProMeet Missions
                        </button>
                      )}

                      <div className="menu_divider" />

                      <button className="menu_item" onClick={() => { navigate("/billing/subscription"); setMenuOpen(false); }}>
                        Abonnement
                      </button>
                      <button className="menu_item" onClick={() => { navigate("/pro/settings"); setMenuOpen(false); }}>
                        Paramètres
                      </button>
                      <button className="menu_item" onClick={() => { logout(); setMenuOpen(false); }}>
                        Déconnexion
                      </button>
                    </>
                  )}

                  {role === "partner" && (
                    <>
                      <button
                        className="menu_item"
                        onClick={() => { navigate("/missions/dashboard"); setMenuOpen(false); }}
                      >
                        <FiTruck style={{ marginRight: 8 }} /> ProMeet Missions
                      </button>
                      <button
                        className="menu_item"
                        onClick={() => { navigate("/partner/mon-dossier"); setMenuOpen(false); }}
                      >
                        Mon dossier Partner
                      </button>
                      <div className="menu_divider" />
                      <button className="menu_item" onClick={() => { logout(); setMenuOpen(false); }}>
                        Déconnexion
                      </button>
                    </>
                  )}

                  {role !== "pro" && role !== "admin" && role !== "partner" && (
                    <>
                      <button className="menu_item menu_item--with-badge" onClick={() => { goRDV(); setMenuOpen(false); }}>
                        <span className="menu_item__row">
                          <FiCalendar style={{ marginRight: 8 }} />
                          <span className="menu_item__label">Mes Rendez-Vous</span>
                          {!isNativeApp && <NavBadge count={badgeRdv} />}
                        </span>
                      </button>
                      <button className="menu_item menu_item--with-badge" onClick={() => { goDocs(); setMenuOpen(false); }}>
                        <span className="menu_item__row">
                          <FiFileText style={{ marginRight: 8 }} />
                          <span className="menu_item__label">Espace Documents</span>
                          {!isNativeApp && <NavBadge count={badgeDocs} />}
                        </span>
                      </button>
                      <div className="menu_divider" />
                      <button className="menu_item" onClick={() => { navigate("/pro/settings"); setMenuOpen(false); }}>
                        Paramètres
                      </button>
                      <button className="menu_item" onClick={() => { logout(); setMenuOpen(false); }}>
                        Déconnexion
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM NAV — App uniquement */}
      {isNativeApp && (
        <nav className="bottom-nav" aria-label="Navigation inférieure">
          <button
            className={`tab ${location.pathname === "/" ? "active" : ""}`}
            onClick={() => navigate("/")}
          >
            <FiHome /><span>Accueil</span>
          </button>

          {!isAuthenticated && (
            <button
              className={`tab ${location.pathname.startsWith("/auth/login") ? "active" : ""}`}
              onClick={() => navigate("/auth/login")}
            >
              <FiUser /><span>Connexion</span>
            </button>
          )}

          {isAuthenticated && role === "pro" && (
            <>
              <button
                className={`tab ${location.pathname.startsWith("/pro/listing") ? "active" : ""}`}
                onClick={goAnnonces}
              >
                <FiBriefcase /><span>Annonces</span>
              </button>
              <button
                type="button"
                className={`tab tab--badged ${location.pathname.startsWith("/pro/calendar") ? "active" : ""}`}
                disabled={!annonceReady || !selectedProfil}
                onClick={() => {
                  if (!annonceReady || !selectedProfil) return;
                  requireProfil(goRDV);
                }}
              >
                <FiCalendar />
                <span>Rendez-vous</span>
                <NavBadge count={badgeRdv} />
              </button>
              <button
                type="button"
                className={`tab tab--badged ${location.pathname.startsWith("/pro/documents") ? "active" : ""}`}
                disabled={!annonceReady || !selectedProfil}
                onClick={() => {
                  if (!annonceReady || !selectedProfil) return;
                  requireProfil(goDocs);
                }}
              >
                <FiFileText />
                <span>Docs</span>
                <NavBadge count={badgeDocs} />
              </button>
              <button
                className={`tab ${location.pathname.startsWith("/pro/profiles/select") ? "active" : ""}`}
                onClick={handleProfilMenuClick}
              >
                <FiUser /><span>Profil</span>
              </button>
            </>
          )}

          {isAuthenticated && role !== "pro" && role !== "admin" && (
            <>
              <button
                type="button"
                className={`tab tab--badged ${location.pathname.includes("mesRdv") || location.pathname.includes("client/appointments") ? "active" : ""}`}
                onClick={goRDV}
              >
                <FiCalendar />
                <span>Mes RDV</span>
                <NavBadge count={badgeRdv} />
              </button>
              <button
                type="button"
                className={`tab tab--badged ${location.pathname.startsWith("/mesdocuments") || location.pathname.startsWith("/pro/documents") ? "active" : ""}`}
                onClick={goDocs}
              >
                <FiFileText />
                <span>Docs</span>
                <NavBadge count={badgeDocs} />
              </button>
              <button
                className={`tab ${location.pathname.startsWith("/pro/settings") ? "active" : ""}`}
                onClick={() => navigate("/pro/settings")}
              >
                <FiUser /><span>Compte</span>
              </button>
            </>
          )}
        </nav>
      )}
    </>
  );
}

export default Header;